import Vue from 'vue'
import Vuex from 'vuex'
import {db, auth, storage, functions} from '../services/firebaseAdapter'
import { OrganizationInterface, OrganizationUserInterface, PostInterface, StateInterface, UserInterface, InvitationInterface, AttachmentInterface, LabelInterface, OrganizationBrandInterface, NotificationInterface, OrganizationPrivateInterface, OrganizationSunsetInterface } from '@/types/interfaces'
import {
  formatSunsetWarningDate,
  isOrgSunsetActive,
  isOrgSunsetWarningVisible,
} from '@/services/orgSunset'
import { flattenedAttachments } from '@/services/flattenAttachments'
import { trackUserProp } from '@/services/analyticsAdapter'

// Initial values
const initialUserState: UserInterface = {
  signedIn: false,
  id: "",
  name: "", 
  email: ""
}
const initialInvitationState: InvitationInterface = {
  id: "",
  name: "",
  email: "",
  role: "reader",
  userId: ""
}
const initialOrganizationState: OrganizationInterface = {
  id: "",
  brand: {
    name: "Ligurio",
    color: "#FF567E",
    logo: "./img/logo-full.png"
  },
  static: {
    shortId: "",
    plan: {
      maxNotifications: 0,
      maxUsers: 0,
    }
  },
  labels: {}
}
const initialPostsState: Array<PostInterface> = []
const initialOrgainzationUser: OrganizationUserInterface = {
  id: "",
  name: "anonym",
  email: "anonym",
  role: "",
  settings: {
    email: {
      allowOrgNotifications: false
    }
  },
  isAuthorized: false,
}

// State

Vue.use(Vuex)

const postsPerPage = 12
const notificationsPerPage = 8
const usersAndInvitationsPerPage = 30

const store = new Vuex.Store({
  state: {
    user: initialUserState,
    invitation: initialInvitationState,
    organization: initialOrganizationState,
    organizationUser: initialOrgainzationUser,
    posts: [],
    latestPostDoc: null,
    loadedAllPosts: false,
    serverTime: undefined,
    private: {
      users: [],
      latestUserDoc: null,
      loadedAllUsers: false,

      invitations: [],
      latestInvitationDoc: null,
      loadedAllInvitations: false,

      notifications: [],
      latestNotificationDoc: null,
      loadedAllNotifications: false,

      data: undefined,
    }
  } as StateInterface,
  mutations: {
    setServerTime(state: StateInterface, payload: Date){ state.serverTime = payload },

    setCurrentUser(state: StateInterface, payload: UserInterface){ state.user = payload },
    unsetUser(state: StateInterface){ state.user = initialUserState },

    setCurrentInvitation(state: StateInterface, payload: InvitationInterface){ state.invitation = payload },
    unsetInvitation(state: StateInterface){ state.invitation = initialInvitationState },

    setOrganization(state: StateInterface, payload: OrganizationInterface){ state.organization = payload },
    unsetOrganization(state: StateInterface){ state.organization = initialOrganizationState },

    setOrganizationUser(state: StateInterface, payload: OrganizationUserInterface){ state.organizationUser = payload },
    unsetOrganizationUser(state: StateInterface){ state.organizationUser = initialOrgainzationUser },

    setPosts(state: StateInterface, payload: Array<PostInterface>){ state.posts = payload },
    appendPosts(state: StateInterface, payload: Array<PostInterface>){ state.posts = state.posts.concat(payload) },
    setLatestPostDocForPagination(state: StateInterface, payload: any){ state.latestPostDoc = payload},
    setLoadedAllPosts(state: StateInterface, areAllPostsLoaded = true){ state.loadedAllPosts = areAllPostsLoaded },
    unsetPosts(state: StateInterface){ 
      state.posts = initialPostsState
      state.latestPostDoc = null
    },

    setOrganizationPrivate(state: StateInterface, payload: OrganizationPrivateInterface){ state.private.data = payload },

    setPrivateNotifications(state: StateInterface, payload: Array<NotificationInterface>){ state.private.notifications = payload },
    appendPrivateNotifications(state: StateInterface, payload: Array<NotificationInterface>){ state.private.notifications = state.private.notifications.concat(payload) },
    setLatestPrivateNotificationDocForPagination(state: StateInterface, payload: any){ state.private.latestNotificationDoc = payload },
    setLoadedAllPrivateNotifications(state: StateInterface, areAllNotificationsLoaded = true){ state.private.loadedAllNotifications = areAllNotificationsLoaded },

    setPrivateUsers(state: StateInterface, payload: Array<OrganizationUserInterface>){ state.private.users = payload },
    appendPrivateUsers(state: StateInterface, payload: Array<OrganizationUserInterface>){ state.private.users = state.private.users.concat(payload) },
    setLatestPrivateUserDocForPagination(state: StateInterface, payload: any){ state.private.latestUserDoc = payload },
    setLoadedAllPrivateUsers(state: StateInterface, areAllUsersLoaded = true){ state.private.loadedAllUsers = areAllUsersLoaded },

    setPrivateInvitations(state: StateInterface, payload: Array<InvitationInterface>){ state.private.invitations = payload },
    appendPrivateInvitations(state: StateInterface, payload: Array<InvitationInterface>){ state.private.invitations = state.private.invitations.concat(payload) },
    setLatestPrivateInvitationDocForPagination(state: StateInterface, payload: any){ state.private.latestInvitationDoc = payload },
    setLoadedAllPrivateInvitations(state: StateInterface, areAllInvitationsLoaded = true){ state.private.loadedAllInvitations = areAllInvitationsLoaded },

    unsetPrivateData(state: StateInterface){ state.private = {
      users: [], latestUserDoc: null, loadedAllUsers: false, 
      invitations: [], latestInvitationDoc: null, loadedAllInvitations: false,
      notifications: [], latestNotificationDoc: null, loadedAllNotifications: false,
      data: undefined,
    }},
  },
  actions: {
    // --------------
    // Authentication
    // --------------
    async updateUserAuth({commit, state}: any){
      const currentUser = await auth.getCurrentUser()
      commit('setCurrentUser', currentUser)
      return state.user
    },
    async signupUser({commit}, payload: {email: string, password: string}){
      try {
        const user : UserInterface | undefined =  await auth.signUp(payload.email, payload.password)
        if(user) commit('setCurrentUser', user)
        else throw("ERROR: User not present in signupUser!")
        return user
      } catch (error) {
        // Set custom error code independence from Firebase:
        throw({
          message: "Signup Failed",
          code: (error.code == "auth/email-already-in-use") ? "signup/email-in-use" : "signup/failed",
          originalError: error
        })
      }
    },
    async signInUser({commit}, payload: {email: string, password: string}){
      const user : UserInterface | undefined = await auth.signIn(payload.email, payload.password)
      if(user) commit('setCurrentUser', user)
      else throw("ERROR: User not present in signInUser!")
      return user
    },
    async signOutUser({commit}){
      await auth.signOut()
      commit('unsetUser')
      commit('unsetOrganizationUser')
      commit('unsetPosts')
      commit('unsetPrivateData')
      return true
    },
    async requestPasswordReset({}, email: string){
      return await auth.requestPasswordReset(email)
    },
    async resetPasswordVerifyCode({}, code: string){
      return await auth.resetPasswordVerifyCode(code)
    },
    async setPassword({}, payload: {code: string, newPassword: string}){
      return await auth.setPassword(payload.code, payload.newPassword)
    },
    async updatePassword({}, payload: {oldPassword: string, newPassword: string}){
      return await auth.updatePassword(payload)
    },
    // --------------
    // Data
    // --------------

    async setServerTime({commit}: any){
      const serverTime = await functions.getServerTime()
      commit('setServerTime', serverTime)
      return true
    },

    // ORGANIZATION

    async setOrganization({commit}: any, {organizationId}){
      const organization: OrganizationInterface | undefined = await db.getOrganization(organizationId)
      if(!organization) return false
      commit('setOrganization', organization)
      return true
    },

    async loadOrganization({commit, dispatch}: any, {organizationId}){
      const setOrganization = await dispatch('setOrganization', {organizationId})
      if(!setOrganization) return false
      commit('unsetOrganizationUser') // User must be authorized again when the organization changes
      return true
    },

    async updateOrganizationLabel({state, dispatch}: any, label: LabelInterface){
      await db.updateOrganizationLabel(state.organization.id, label)
      // Update local labels list:
      await dispatch('setOrganization', {organizationId: state.organization.id})
      return true
    },

    async updateLabelPositions({state, dispatch}: any, labelsPositions: {[key: string]: number}){
      await db.updateLabelPositions(state.organization.id, labelsPositions)
      // Update local labels list:
      await dispatch('setOrganization', {organizationId: state.organization.id})
      return true
    },

    async uploadOrganizationLabelImg({}, payload: {file: File, path: string}){
      if(!payload.file) throw("No file to upload!")

      return storage.upload(
        payload.path,
        payload.file
      )
    },

    async updateOrganizationSunset({ state, commit }, payload: { organizationId: string, sunset: OrganizationSunsetInterface }){
      await db.updateOrganizationSunset(payload.organizationId, payload.sunset)
      if (state.organization.id === payload.organizationId) {
        commit('setOrganization', {
          ...state.organization,
          sunset: payload.sunset,
        })
      }
      return true
    },

    async loadOrganizationSunset({}, organizationId: string){
      return await db.getOrganizationSunset(organizationId)
    },

    async updateOrganizationBrand({state, dispatch}, payload: OrganizationBrandInterface){
      await db.updateOrganizationBrand(state.organization.id, payload)
      // Update current organization version from server:
      await dispatch('setOrganization', {organizationId: state.organization.id})
      return true
    },
    async uploadOrganizationLogo({}, payload: {file: File, path: string}){
      if(!payload.file) throw("No file to upload!")

      return storage.upload(
        payload.path,
        payload.file
      )
    },

    // ORGANIZATION PRIVATE DATA

    async getOrganizationPrivate({state, commit}: any){
      const privateData: OrganizationPrivateInterface | undefined = await db.getOrganizationPrivate(state.organization.id)
      if(!privateData) return false
      commit('setOrganizationPrivate', privateData)
      return true
    },

    async updateOrganizationPrivate({state, dispatch}, payload: OrganizationPrivateInterface){
      await db.updateOrganizationPrivate(state.organization.id, payload)
      // Update local private data:
      await dispatch('getOrganizationPrivate', {organizationId: state.organization.id})
      return true
    },


    // POSTS

    async getPosts(
      {state, commit}: any, 
      payload: {
        filter: {
          labelIds?: string[],
          releasedAtStart?: string,
          releasedAtEnd?: string,
        },
        isLoadMore: boolean,
      } = {
        filter: {},
        isLoadMore: false,
      } 
    ){
      try {
        if(!state.organization.id) throw("Failed loading posts. No organization ID present")
        // GET posts
        const getPostsResponse = await db.getPosts(
          state.organization.id, 
          payload.filter, 
          postsPerPage,
          (payload.isLoadMore && state.latestPostDoc) ? state.latestPostDoc : null
        )
        const posts = getPostsResponse.posts
        const lastPost = getPostsResponse.lastPost
        // Set last post for pagination
        commit('setLatestPostDocForPagination', lastPost)
        // Set all posts loaded
        commit('setLoadedAllPosts', posts.length < postsPerPage)
        // Set downloadURLs for attachments

        const postsWithAttachments = await Promise.all(posts.map(async post => {
          post.attachments = (await Promise.all(post.attachments.map(async attachment => {
            // GET download URL from Firebase for PDFs. Images have them already on the "preview"
            attachment.downloadUrl = attachment.type == "pdf" ? 
              await storage.get(attachment.path).catch(error => {
                console.error('ERROR in storage.get()', error)
                return ''
              }) :
              attachment.preview
            return attachment
          }))).filter(postAttachment => postAttachment.downloadUrl) // Filter out attachments that had an error and have no downloadURL
          return post
        }))
        commit(
          payload.isLoadMore ? 'appendPosts' : 'setPosts', 
          postsWithAttachments
        )
        return posts
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async createPost({state, commit}: any, post: PostInterface){
      return db.createPost(state.organization.id, post)
    },
    async updatePost({state, commit}: any, post: PostInterface){
      return db.updatePost(state.organization.id, post.id, post)
    },
    async deletePost({state, commit}: any, post: PostInterface){
      try {
        // Delete post in database:
        await db.deletePost(state.organization.id, post.id)
        // Delete file uploads associated with the post: 
        if(post.attachments) await _deleteAttachments(post.attachments)
        // Delete body attachment uploads associated with the post: 
        if(post.bodyAttachments) await _deleteAttachments(post.bodyAttachments)
        // Remove from local state: 
        commit(
          'setPosts', 
          state.posts.filter((currentPost: PostInterface) => {
            return currentPost.id != post.id
          })
        )
        return state.posts
      } catch (error) {
        console.error(error)
        return false
      }
    },

    // POST ATTACHMENT

    async addPostAttachment(
      {}, 
      attachmentDraft: AttachmentInterface
    ){
      if(!attachmentDraft.file) throw("No file for attachment found!")

      return storage.upload(
        attachmentDraft.path,
        attachmentDraft.file
      )
    },
    async addPostBodyAttachment({state}: any, payload: {
      fileId: string,
      file: File
    }){
      const shortId = state.organization.static.shortId
      const fileExt = payload.file.name.split('.').pop()

      const path = `${shortId}/posts/attachments/${payload.fileId}.${fileExt}`
      
      return storage.upload(
        path,
        payload.file
      )
    },
    async removePostAttachment({}: any, path: string){
      return storage.delete(path)
    },

    // USERS

    async loadOrganizationUser({state, commit}: any, userId: string){
      try {
        // Get user from the database
        const organizationUser = await db.getUser(state.organization.id, userId)
        commit('setOrganizationUser', organizationUser)
        trackUserProp({
          organization: state.organization.id,
          organizationRole: organizationUser.role,
        })
        return organizationUser
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async listenUntilOrganizationUserLoaded({state, commit}: any, userId: string){
      const organizationUser = await db.listenUntilGetUser(state.organization.id, userId)
      commit('setOrganizationUser', organizationUser)
      return organizationUser
    },
    async loadOrganizationUsers(
      {state, commit}: any, 
      payload = {
        isLoadMore: false as Boolean
      }
    ){
      try {
        if(!state.organization.id) throw("Failed loading user. No organization ID present")
        const usersResponse = await db.getOrganizationUsers(
          state.organization.id,
          usersAndInvitationsPerPage,
          (payload.isLoadMore && state.private.latestUserDoc) ? state.private.latestUserDoc : null
        )

        const users = usersResponse.users
        const lastUser = usersResponse.lastUser

        // Set last user for pagination
        commit('setLatestPrivateUserDocForPagination', lastUser)

        // Set all users loaded
        commit('setLoadedAllPrivateUsers', users.length < usersAndInvitationsPerPage)

        commit(
          payload.isLoadMore ? 'appendPrivateUsers' : 'setPrivateUsers', 
          users
        )
        return users
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async updateUser({state}: any, user: OrganizationUserInterface){
      try {
        // Update user
        await db.updateUser(state.organization.id, user)
        // Update user in local state
        this.commit(
          'setPrivateUsers',
          state.private.users.map((mappedUser: OrganizationUserInterface) => {
            // Iterate over all invitation in state and for the one being updated return the new one. 
            return mappedUser.id == user.id ? user : mappedUser
          })
        )
        return state.private.users
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async deleteUser({state, commit}: any, userId: string){
      try {
        // Delete in database
        await db.deleteUser(state.organization.id, userId)
        // Remove from local state: 
        commit(
          'setPrivateUsers', 
          state.private.users.filter((user: OrganizationUserInterface) => {
            return user.id != userId
          })
        )
        return state.private.users
      } catch (error) {
        console.error(error)
        return false
      }
    },

    // NOTIFICATIONS

    async getNotifications(
      {state, commit}: any, 
      payload: {
        isLoadMore: boolean,
      } = {
        isLoadMore: false,
      } 
    ){
      try {
        if(!state.organization.id) throw("Failed loading notifications. No organization ID present")
        // GET notifications
        const getNotificationsResponse = await db.getNotifications(
          state.organization.id,
          notificationsPerPage,
          (payload.isLoadMore && state.private.latestNotificationDoc) ? state.latestNotificationDoc : null
        )

        const notifications = getNotificationsResponse.notifications
        const lastNotification = getNotificationsResponse.lastNotification
        // Set last notification for pagination
        commit('setLatestPrivateNotificationDocForPagination', lastNotification)
        // Set all notifications loaded
        commit('setLoadedAllPrivateNotifications', notifications.length < notificationsPerPage)
        
        commit(
          payload.isLoadMore ? 'appendPrivateNotifications' : 'setPrivateNotifications', 
          notifications
        )
        return notifications
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async createNotification({state, commit}: any, notification: NotificationInterface){
      return db.createNotification(state.organization.id, notification)
    },

    // INVITATIONS 

    async loadOrganizationInvitations(
      {state, commit}: any, 
      payload = {
        isLoadMore: false as Boolean
      }
    ){
      try {
        if(!state.organization.id) throw("Failed loading user. No organization ID present")
        const invitationResponse = await db.getOrganizationInvitations(
          state.organization.id,
          usersAndInvitationsPerPage,
          (payload.isLoadMore && state.private.latestInvitationDoc) ? state.private.latestInvitationDoc : null
        )

        const invitations = invitationResponse.invitations
        const lastInvitation = invitationResponse.lastInvitation

        // Set last user for pagination
        commit('setLatestPrivateInvitationDocForPagination', lastInvitation)

        // Set all users loaded
        commit('setLoadedAllPrivateInvitations', invitations.length < usersAndInvitationsPerPage)

        commit(
          payload.isLoadMore ? 'appendPrivateInvitations' : 'setPrivateInvitations', 
          invitations
        )
        return invitations
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async loadInvitation({state, commit}: any, invitationId: string){
      try {
        if(!state.organization.id) throw("Failed loading invitation. No organization ID present")
        const invitation: InvitationInterface | undefined = await db.getOrganizationInvitation(state.organization.id, invitationId)
        commit('setCurrentInvitation', invitation)
        return invitation
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async createInvitation({state}: any, invitation: InvitationInterface){
      // TODO: Block duplicate invitation email addresses
      return await db.createInvitation(state.organization.id, invitation)
    },
    async updateInvitationRole({state}: any, payload: {
      invitationId: string, 
      role: "reader" | "author" | "admin"
    }){
      try {
        // Update invitation with user role
        await db.updateInvitationRole(state.organization.id, payload.invitationId, payload.role)
        // Update invitation in local state
        this.commit(
          'setPrivateInvitations',
          state.private.invitations.map((mappedInvitation: InvitationInterface) => {
            // Iterate over all invitation in state and for the one being updated return the new one. 
            if(mappedInvitation.id == payload.invitationId) mappedInvitation.role = payload.role        
            return mappedInvitation
          })
        )
        return state.private.invitations
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async updateInvitationUser({state, commit}: any, userDraft: InvitationInterface){
      if(!state.invitation.id) throw("Failed updating invitation without ID")
      // Update invitation record
      await db.updateInvitationUser(state.organization.id, state.invitation.id, userDraft)
      commit('unsetInvitation')
    },
    async deleteInvitation({state, commit}: any, invitationId: string){
      try {
        // Delete in database
        await db.deleteInvitation(state.organization.id, invitationId)
        // Remove from local state: 
        commit(
          'setPrivateInvitations', 
          state.private.invitations.filter((invitation: InvitationInterface) => {
            return invitation.id != invitationId
          })
        )
        return state.private.invitations
      } catch (error) {
        console.error(error)
        return false
      }
    },
  },
  getters: {
    userSignedIn: state => state.user.signedIn,
    userId: state => state.user.id,
    userName: state => state.organizationUser.name || state.user.name || "anonym",
    userEmail: state => state.user.email,
    invitation: state => state.invitation,
    organization: state => state.organization,
    organizationColor: state => state.organization.brand.color,
    organizationName: state => state.organization.brand.name,
    organizationUser: state => state.organizationUser,
    organizationShortId: state => state.organization.static.shortId,
    organizationLabels: state => state.organization.labels,
    organizationLabelsAsArray: state => { // convert object to array of objects and sort by "position"
      return Object.keys(state.organization.labels)
        .map(key => state.organization.labels[key])
        .sort((l1, l2) => (l1.position > l2.position) ? 1 : ((l2.position > l1.position) ? -1 : 0) )
    },
    userIsAuthorOrAdmin: state => ["author", "admin"].includes(state.organizationUser.role),
    userIsAdmin: state => state.organizationUser.role == "admin",
    serverTime: state => state.serverTime,
    posts: state => state.posts,
    loadedAllPosts: state => state.loadedAllPosts,
    loadedAllPrivateNotifications: state => state.private.loadedAllNotifications,
    loadedAllPrivateUsers: state => state.private.loadedAllUsers,
    loadedAllPrivateInvitations: state => state.private.loadedAllInvitations,
    organizationPrivate: state => state.private.data,
    privateNotifications: state => state.private.notifications,
    privateUsers: state => state.private.users,
    privateInvitations: state => state.private.invitations,
    organizationSunset: state => state.organization.sunset,
    isOrgSunsetActive: state => isOrgSunsetActive(
      state.organization.sunset,
      state.serverTime || new Date()
    ),
    isOrgSunsetWarningVisible: state => isOrgSunsetWarningVisible(
      state.organization.sunset,
      undefined,
      state.serverTime || new Date()
    ),
    sunsetWarningFormattedDate: state => formatSunsetWarningDate(state.organization.sunset),
  },
  modules: {}
})

export default store


// PRIVATE METHODS

async function _deleteAttachments(attachments:Array<AttachmentInterface>){
  return await Promise.all(
    flattenedAttachments(attachments).map((attachment: AttachmentInterface) => {
      return storage.delete(attachment.path)
    })
  )
}
